import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme, keyframes } from 'styled-components';
import { rem } from 'polished';
import { __, __r } from 'react-i18n';
import { SvgIcon, IconType } from '../../General/SvgIcon';
import {
  FlexCol,
  FlexRowCenter,
  FlexColCenter,
  FlexRowCenterVertical,
  Flexbox,
} from '../../../Defaults/Flexbox/Flexbox';
import { OutsideClick } from '../../../Defaults/OutsideClick';
import { TextP } from '../../../Defaults/Text';
import { Button } from '../../General/Button';
import { ModalItemsTable } from './ModalItemsTable';
import { getProductDetailInfo } from '../../../../utilities/selectors';
import { round } from '../../../../utilities';

interface Props {
  handleClose: any;
  isActive: boolean;
  redirectToCart: () => void;
  addToCart: (count: number) => void;
  product: any;
  initCount: number;
}

export const Modal = ({
  handleClose,
  isActive,
  redirectToCart,
  addToCart,
  product,
  initCount,
}: Props) => {
  const info = getProductDetailInfo(product);
  const { unit } = info;
  const step = unit === 'm' ? 0.1 : 1;
  const [inCartCount, setInCartCount] = React.useState(initCount);
  const [count, setCount] = React.useState(initCount.toString());

  const handleKeyDown = (event): void => {
    // 27 is a code for Escape button
    if (event.which === 27) {
      handleClose();
    }

    if (event.which === 13) {
      redirectToCart();
    }
  };

  const onKeyDown = () => {
    React.useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    });
  };

  const handleCountChange = (newCount: string) => {
    const withoutCommas = newCount.replace(',', '.');
    let newCountNumber = parseFloat(withoutCommas);

    if (isNaN(newCountNumber)) {
      setCount(newCount);
      return;
    }

    if (parseFloat(newCount) < step) {
      newCount = step.toFixed(3);
    }
    newCountNumber = parseFloat(newCount);
    const countDiff: number = -(inCartCount - newCountNumber);
    addToCart(round(countDiff, 3));
    setInCartCount(newCountNumber);
    setCount(newCount);
  };

  if (!isActive) {
    return null;
  }

  return (
    <div>
      <ModalStyle />
      <ModalHolder>
        <OutsideClickWrapper>
          <OutsideClick handleFunction={handleClose}>
            <Wrapper handleClick={onKeyDown()}>
              <TitleWrapper>
                <Title>{__('Produkt bol pridaný do košíka')}</Title>
                <SvgIcon
                  width={24}
                  height={24}
                  icon={IconType.close}
                  alt="Zavrieť okno"
                  title="Zavrieť okno"
                  cursor={'pointer'}
                  fill={'black'}
                  onClick={handleClose}
                />
              </TitleWrapper>
              <ModalItemsTable
                count={count}
                handleCountChange={handleCountChange}
                products={[product]}
                isFetching={false}
              />
              <ButtonsCover>
                <ModalBackButtonWrapper>
                  <SvgIcon
                    width={16}
                    height={16}
                    viewBox={'0 0 16 16'}
                    icon={IconType.leftArrow}
                    fill={'black'}
                    cursor={'pointer'}
                    marginRight={8}
                  />
                  <ModalBackButton onClick={handleClose}>
                    {__('Pokračovať v nakupovaní')}
                  </ModalBackButton>
                </ModalBackButtonWrapper>
                <CartButton onClick={redirectToCart}>
                  <SvgIcon
                    width={24}
                    height={24}
                    icon={IconType.cart}
                    fill={'#FFFFFF'}
                    alt={__('Košík')}
                    title={__('Košík')}
                    marginRight={8}
                  />
                  <Text>{__('Dokončiť nákup')}</Text>
                </CartButton>
              </ButtonsCover>
            </Wrapper>
          </OutsideClick>
        </OutsideClickWrapper>
      </ModalHolder>
    </div>
  );
};

const CartButton = styled(Button)`
  background: ${({ theme }) => theme.colors.secondary};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.secondaryOnHover};
    outline: none;
  }
`;

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const ModalStyle = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.black};
  opacity: 0.2;
  z-index: ${({ theme }) => theme.index.modal};
`;

const ModalHolder = styled(FlexColCenter)`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ theme }) => theme.index.modal};
`;

const OutsideClickWrapper = styled.div`
  width: 100%;
  max-width: ${rem(564)};
`;

const Text = styled(TextP)`
  display: inline-block;
  color: ${({ theme }) => theme.colors.white};
`;

const ModalBackButtonWrapper = styled(FlexCol)`
  display: inline-block;
  margin-right: ${rem(88)};

  ${({ theme }) => theme.mediab.m580`
    order: 2;
    margin-right: 0;
    margin-top: ${rem(31)};
  `};
`;

const Wrapper = styled(FlexCol)`
  padding: ${rem(26)} ${rem(24)} ${rem(24)};
  width: 100%;
  position: relative;
  opacity: 1;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${rem(4)};
  box-shadow: ${({ theme }) => theme.boxShadow.default};
  display: block;
  animation: ${animateIn} 0.15s ease;

  ${({ theme }) => theme.mediab.m580`
      padding: ${rem(24)} ${rem(24)} ${rem(40)};
      width: initial;
  `};
`;

const TitleWrapper = styled(FlexRowCenter)`
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${rem(24)};
`;

const Title = styled.h3`
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(16)};
  line-height: ${rem(24)};
  margin: 0;
  display: inline-flex;
  align-items: center;

  ${({ theme }) => theme.mediab.m580`
    max-width: ${rem(180)};
  `};
`;

const ButtonsCover = styled(Flexbox)`
  margin-top: ${rem(24)};
  align-items: center;
  flex-direction: row;

  ${({ theme }) => theme.mediab.m580`
    flex-direction: column;
  `};
`;

const ModalBackButton = styled(Link)`
  display: inline-block;
  color: ${({ theme }) => theme.colors.textPrimary};
  border: none;
  margin-bottom: 0;
  font-weight: 500;
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  text-decoration: underline;
`;
